import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { ListItem } from "@/components/campaign/ListItem";
import { Button } from "@/components/generic/Button";
import { Select, SelectItem } from "@/components/generic/Select";
import { TextField } from "@/components/generic/TextField";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { TextOperator, textOperators } from "@/utils/operators";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "react-aria-components";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

export type StringFilterItem = {
  value: string;
};

const addItemSchema = z.object({
  value: z.string().min(1, "Tekst er påkrævet"),
});

const formSchema = z.object({
  operator: textOperators.enum,
  items: z.array(addItemSchema).min(1, "Mindst ét kriterie skal tilføjes"),
});

export function StringFilter({
  group: { title, description },
  formId,
  queryFilters,
  update,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {}
>) {
  const stringFilter = queryFilters.value;

  const {
    control: addItemControl,
    handleSubmit: handleAddItem,
    reset: resetAddItem,
  } = useForm({
    resolver: zodResolver(addItemSchema),
    defaultValues: {
      value: "",
    },
  });

  const {
    control: formControl,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { isDirty },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      operator: (stringFilter?.operator || "Contains") as TextOperator,
      items: stringFilter?.values?.map((value) => ({ value })) || [],
    },
  });

  useReportDirty(isDirty, setIsDirty);

  const operator = watch("operator");

  const { fields, append, remove } = useFieldArray({
    control: formControl,
    name: "items",
  });

  const onAddItem = handleAddItem((data) => {
    append(data);
    resetAddItem();
  });

  const onSubmit = handleFormSubmit((data) => {
    update({
      value: {
        operator: data.operator,
        values: data.items.map((item) => item.value),
      },
    });
  });

  return (
    <div className="flex h-full max-w-max flex-col items-center justify-between">
      <form onSubmit={onSubmit} id={formId} />
      <div className="flex flex-col items-center gap-y-9">
        <SegmentHeader title={title} description={description} />
        <div className="flex w-full items-stretch gap-4">
          <Controller
            control={formControl}
            name="operator"
            render={({ field, fieldState }) => (
              <Select
                placeholder="Vælg operator"
                aria-label="Operator"
                selectedKey={field.value}
                onSelectionChange={field.onChange}
                items={Object.entries(textOperators.values).map(
                  ([key, value]) => ({
                    id: key,
                    label: value,
                  }),
                )}
                errorMessage={fieldState.error?.message}
              >
                {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
              </Select>
            )}
          />
          <Form onSubmit={onAddItem} className="flex grow gap-x-4">
            <TextField
              className="shrink-0 grow"
              name="value"
              control={addItemControl}
              aria-label={title}
              placeholder="Indsæt ord"
            />
            <Button size="input" type="submit">
              Tilføj
            </Button>
          </Form>
        </div>
        <ul className="flex w-full flex-col gap-y-2">
          {fields.map((field, index) => (
            <ListItem
              key={field.id}
              label={`${textOperators.values[operator]} "${field.value}"`}
              onRemove={() => remove(index)}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}
